import {Progress} from '@base-ui/react/progress';

import {CheckmarkIcon} from '@/components/Icons';
import {twx} from '@/utils/tailwindUtils';

export type ProgressBarProps = {
    totalSteps: number;
    finishedSteps: number;
    className?: string;
    'aria-label'?: string;
};

const MAX_DOTS_STEPS = 5;

const segmentGradient = (index: number) =>
    index % 2 === 0
        ? 'linear-gradient(90deg, var(--color-malibu), var(--color-heliotrope))'
        : 'linear-gradient(90deg, var(--color-heliotrope), var(--color-malibu))';

const nodeColor = (index: number) => (index % 2 === 0 ? 'var(--color-malibu)' : 'var(--color-heliotrope)');

const Segment = ({index, filled}: {index: number; filled: boolean}) => (
    <div
        className='-mx-0.5 h-2 flex-1'
        style={{background: filled ? segmentGradient(index) : 'var(--color-primary-dark)'}}
    />
);

const NodeCompleted = ({index}: {index: number}) => (
    <div
        className='relative z-10 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full p-[2px]'
        style={{background: nodeColor(index)}}
    >
        <div className='flex h-full w-full items-center justify-center rounded-full bg-romance'>
            <CheckmarkIcon />
        </div>
    </div>
);

const currentNodeBackground = (index: number, hasNext?: boolean, active = true) => {
    if (!active) return 'var(--color-primary-dark)';
    return hasNext ? `linear-gradient(90deg, ${nodeColor(index)}, var(--color-primary-dark))` : nodeColor(index);
};

const NodeCurrent = ({index, hasNext, active = true}: {index: number; hasNext?: boolean; active?: boolean}) => (
    <div
        className='relative z-10 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full p-[2px]'
        style={{background: currentNodeBackground(index, hasNext, active)}}
    >
        <div className='h-full w-full rounded-full bg-romance' />
    </div>
);

const NodeFuture = () => (
    <div className='relative z-10 h-[18px] w-[18px] shrink-0 rounded-full border-2 border-primary-dark' />
);

const SingleStepProgressBar = ({finishedSteps, totalSteps, className, 'aria-label': ariaLabel}: ProgressBarProps) => {
    const isCompleted = finishedSteps >= totalSteps;
    const isStarted = finishedSteps > 0;
    return (
        <Progress.Root
            aria-label={ariaLabel}
            value={Math.min(Math.max(finishedSteps, 0), totalSteps)}
            min={0}
            max={totalSteps}
            className={twx('flex items-center', className)}
        >
            <NodeCurrent index={0} hasNext={!isCompleted} active={isStarted} />
            <Segment index={0} filled={isCompleted} />
            {isCompleted ? <NodeCompleted index={1} /> : <NodeFuture />}
        </Progress.Root>
    );
};

const StepProgressBar = ({finishedSteps, totalSteps, className, 'aria-label': ariaLabel}: ProgressBarProps) => {
    const safeFinished = Math.min(Math.max(finishedSteps, 0), totalSteps);

    const nodes = Array.from({length: totalSteps}, (_, i) => {
        const isCompleted = i < safeFinished;
        const isCurrent = i === safeFinished;
        const isStarted = safeFinished > 0;

        return (
            <div key={`node-${i}`} className='contents'>
                {i > 0 && <Segment index={i - 1} filled={i - 1 < safeFinished} />}
                {isCompleted ? (
                    <NodeCompleted index={i} />
                ) : isCurrent ? (
                    <NodeCurrent index={i} hasNext={i < totalSteps - 1} active={isStarted} />
                ) : (
                    <NodeFuture />
                )}
            </div>
        );
    });

    return (
        <Progress.Root
            aria-label={ariaLabel}
            value={safeFinished}
            min={0}
            max={totalSteps}
            className={twx('flex items-center', className)}
        >
            {nodes}
        </Progress.Root>
    );
};

const FillProgressBar = ({finishedSteps, totalSteps, className, 'aria-label': ariaLabel}: ProgressBarProps) => {
    const safeFinished = Math.min(Math.max(finishedSteps, 0), totalSteps);
    const percent = Math.round((safeFinished / totalSteps) * 100);

    return (
        <Progress.Root
            aria-label={ariaLabel}
            value={safeFinished}
            min={0}
            max={totalSteps}
            className={twx('h-2 w-full overflow-hidden rounded-full bg-primary-dark', className)}
        >
            <div
                className='h-full rounded-full'
                style={{
                    width: `${percent}%`,
                    background: 'linear-gradient(90deg, var(--color-malibu), var(--color-heliotrope))',
                }}
            />
        </Progress.Root>
    );
};

/**
 * Step-based progress bar — nodes connected by gradient segments.
 * Single-step variant renders start→end (one segment between two nodes) so the
 * bar is still readable when there's only one action.
 * Falls back to a smooth fill bar when totalSteps exceeds MAX_DOTS_STEPS.
 */
const ProgressBar = (props: ProgressBarProps) => {
    if (props.totalSteps <= 0) return null;
    if (props.totalSteps > MAX_DOTS_STEPS) return <FillProgressBar {...props} />;
    if (props.totalSteps === 1) return <SingleStepProgressBar {...props} />;
    return <StepProgressBar {...props} />;
};

export default ProgressBar;
